import api from './client'
import type { Application, BoardingReadiness, NorthBoardingDryRunResult, NorthBoardingSubmissionSummary } from '../api'

export const applicationApi = {
  get: async (leadId: string): Promise<Application> => {
    const response = await api.get<Application>(`/leads/${leadId}/application`)
    return response.data
  },

  create: async (leadId: string): Promise<Application> => {
    const response = await api.post<Application>(`/leads/${leadId}/application`)
    return response.data
  },

  updateStep: async (applicationId: string, step: number, data: Record<string, unknown>): Promise<Application> => {
    const response = await api.put<Application>(`/applications/${applicationId}/step/${step}`, data)
    return response.data
  },

  finalize: async (applicationId: string): Promise<Application> => {
    const response = await api.post<Application>(`/applications/${applicationId}/finalize`)
    return response.data
  },

  boardingReadiness: async (applicationId: string): Promise<BoardingReadiness> => {
    const response = await api.get<BoardingReadiness>(`/applications/${applicationId}/boarding-readiness`)
    return response.data
  },

  /**
   * Send the application's MPA to BoldSign for signature.
   *
   * Returns 202 Accepted with `{ application_id, status: 'queued' }`.
   * The actual envelope creation happens asynchronously on the `high`
   * queue. Watch the application's `boldsign_document_id` /
   * `boldsign_sent_at` fields (or the lead's pipeline stage) for
   * progress updates pushed by Reverb.
   *
   * @see CLAUDE.md Section 10 — single-signer BoldSign model
   */
  sendForSignature: async (
    applicationId: string,
  ): Promise<{ application_id: string; status: string; message: string }> => {
    const response = await api.post(
      `/applications/${applicationId}/send-for-signature`,
    )
    return response.data
  },

  submitWithUploadedDocs: async (
    applicationId: string,
    note?: string,
  ): Promise<{ application_id: string; lead_id: string; stage: string; message: string }> => {
    const response = await api.post(
      `/applications/${applicationId}/submit-with-uploaded-docs`,
      note ? { note } : {},
    )
    return response.data
  },

  updateFiservMid: async (
    applicationId: string,
    payload: { fiserv_mid: string; reason?: string },
  ): Promise<Application> => {
    const response = await api.patch<Application>(
      `/applications/${applicationId}/fiserv-mid`,
      payload,
    )
    return response.data
  },

  /**
   * Poll the BoldSign job status for an application.
   *
   * Returns one of:
   *   - status='pending'  — job is queued / running
   *   - status='sent'     — envelope dispatched, document_id populated
   *   - status='failed'   — job failed, `error` holds the reason
   *   - status=null       — no send attempt on record
   *
   * Used by Step 7 after it receives 202 from sendForSignature to
   * avoid showing "success" while the async job is still running or
   * has already failed silently.
   */
  getEsignStatus: async (
    applicationId: string,
  ): Promise<{
    status: 'pending' | 'sent' | 'failed' | null
    error: string | null
    document_id: string | null
    sent_at: string | null
  }> => {
    const response = await api.get(
      `/applications/${applicationId}/esign-status`,
    )
    return response.data
  },

  /**
   * Resend a BoldSign envelope — optionally after changing the
   * signer email. Voids the existing envelope (if any), clears
   * boldsign_* tracking, dispatches a fresh send, records a
   * pipeline_event for audit.
   *
   * Pass `owner_email` to update the signer address before resending.
   * Omit it to resend to the same address (typical "they didn't
   * receive it" case).
   *
   * Returns 202; poll getEsignStatus for resolution just like the
   * initial send.
   */
  resendSignature: async (
    applicationId: string,
    ownerEmail?: string,
  ): Promise<{ application_id: string; status: string; message: string }> => {
    const response = await api.post(
      `/applications/${applicationId}/resend-signature`,
      ownerEmail ? { owner_email: ownerEmail } : {},
    )
    return response.data
  },

  voidSignature: async (
    applicationId: string,
    reason?: string,
  ): Promise<{ application: Application; message: string }> => {
    const response = await api.post(
      `/applications/${applicationId}/void-signature`,
      reason ? { reason } : {},
    )
    return response.data
  },

  /**
   * Override ValidiFI bank-validation flags on a flagged application.
   *
   * Per CLAUDE.md Section 11 the override requires:
   *   - Operations / management / super_admin role (server enforces)
   *   - A non-trivial note (min 10 chars; server enforces)
   * Both halves are validated server-side; the override is logged
   * to `audit_logs` with the user id, IP, and the previous flag set.
   *
   * On success, the application's `validifi_passed` flips to true
   * and the verification → processor_submission gate unblocks.
   *
   * @see CLAUDE.md Section 11 — ValidiFI override workflow
   */
  overrideValidiFI: async (
    applicationId: string,
    note: string,
  ): Promise<{
    application_id: string
    validifi_passed: true
    overridden_by: string
    note: string
    message: string
  }> => {
    const response = await api.post(
      `/applications/${applicationId}/validifi/override`,
      { note },
    )
    return response.data
  },

  /**
   * Re-run the ValidiFI bank validation on an application.
   *
   * Used when banking info has been corrected after a flagged check,
   * or when ValidiFI data sources were temporarily unavailable.
   *
   * Authorization (server-enforced): operations / management /
   * super_admin. Sales reps receive 403.
   *
   * @see CLAUDE.md Section 11 — ValidiFI Bank Validation Integration
   */
  recheckValidiFI: async (
    applicationId: string,
  ): Promise<{
    application_id: string
    status: 'queued'
    message: string
  }> => {
    const response = await api.post(
      `/applications/${applicationId}/validifi/recheck`,
    )
    return response.data
  },

  generateFiservReturnUploadLink: async (
    applicationId: string,
  ): Promise<{ message: string; upload_url: string; expires_at: string | null }> => {
    const response = await api.post(
      `/applications/${applicationId}/fiserv/return-upload-link`,
    )
    return response.data
  },

  resubmitToFiserv: async (
    applicationId: string,
    data: { note: string; document_ids?: string[] },
  ): Promise<{ message: string; application: Application }> => {
    const response = await api.post(
      `/applications/${applicationId}/fiserv/resubmit`,
      data,
    )
    return response.data
  },

  /**
   * Build the North XML and validate it against the production FACS
   * XSD. The response does not include the document, and no boarding
   * request is sent.
   */
  northDryRun: async (applicationId: string): Promise<NorthBoardingDryRunResult> => {
    const response = await api.post<NorthBoardingDryRunResult>(
      `/applications/${applicationId}/north/dry-run`,
    )
    return response.data
  },

  /**
   * Ask for a new North ExtRefID. A funding-bank change cannot be
   * fixed by retrying the previous submission.
   */
  northReboard: async (
    applicationId: string,
    reason: string,
  ): Promise<{ message: string; request_id: string | null; ext_ref_id: string | null; as_ref_id: string | null }> => {
    const response = await api.post(
      `/applications/${applicationId}/north/reboard`,
      { reason },
    )
    return response.data
  },

  northSubmissions: async (applicationId: string): Promise<{ data: NorthBoardingSubmissionSummary[] }> => {
    const response = await api.get<{ data: NorthBoardingSubmissionSummary[] }>(
      `/applications/${applicationId}/north/submissions`,
    )
    return response.data
  },
}
